import { supabase } from './supabase';
import { userGalleryService } from './userGalleryService';
import { furnitureRemovalService } from './furnitureRemovalService';

export interface UserImage {
  id: string;
  url: string;
  type: 'moodboard' | 'upstaging-original' | 'upstaging-result' | 'touchup-original' | 'touchup-result' | 'user-upload' | 'furniture-original' | 'furniture-result';
  serviceType: 'moodboard' | 'upstaging' | 'touchup' | 'gallery' | 'furniture';
  createdAt: string;
  prompt?: string;
  description?: string;
}

export const userImageService = {
  // Debug image URL accessibility
  async debugImageUrl(url: string): Promise<void> {
    console.log('🔍 Debugging image URL:', url);
    
    try {
      // Test direct fetch
      const response = await fetch(url, { mode: 'cors' });
      console.log('📡 Fetch status:', response.status, response.statusText);
      console.log('📡 CORS headers:', response.headers.get('access-control-allow-origin'));
      
      // Test image loading
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        console.log('✅ Image loaded successfully');
        console.log('📏 Image dimensions:', img.naturalWidth, 'x', img.naturalHeight);
      };
      
      img.onerror = () => {
        console.error('❌ Image failed to load');
      };
      
      img.src = url;
    } catch (error) {
      console.error('❌ Debug error:', error);
    }
  },

  // Fetch all user's images from all services
  async getUserImages(userId: string): Promise<UserImage[]> {
    try {
      const images: UserImage[] = [];

      // Fetch moodboard generations (primary images)
      const { data: moodboardGenerations, error: moodboardError } = await supabase
        .from('moodboard_generations')
        .select('id, result_image_url, created_at')
        .eq('user_id', userId)
        .eq('status', 'completed')
        .not('result_image_url', 'is', null)
        .order('created_at', { ascending: false });

      if (moodboardError) {
        console.error('Error fetching user moodboard images:', moodboardError);
      } else if (moodboardGenerations) {
        // Add primary images from moodboard generations
        moodboardGenerations.forEach(gen => {
          if (gen.result_image_url) {
            images.push({
              id: `moodboard-primary-${gen.id}`,
              url: gen.result_image_url,
              type: 'moodboard',
              serviceType: 'moodboard',
              createdAt: gen.created_at,
              description: 'Moodboard Generation (Primary)'
            });
          }
        });
      }

      // Fetch additional moodboard generation images
      try {
        // First get the generation IDs for this user
        const generationIds = moodboardGenerations?.map(gen => gen.id) || [];
        
        if (generationIds.length > 0) {
          const { data: additionalMoodboardImages, error: additionalError } = await supabase
            .from('moodboard_generation_images')
            .select('id, image_url, image_name, is_primary, sort_order, created_at, generation_id')
            .in('generation_id', generationIds)
            .order('created_at', { ascending: false })
            .order('sort_order');

          if (additionalError) {
            console.warn('Additional moodboard images table not available:', additionalError);
          } else if (additionalMoodboardImages && additionalMoodboardImages.length > 0) {
            console.log(`Found ${additionalMoodboardImages.length} additional moodboard images`);
            additionalMoodboardImages.forEach((img) => {
              images.push({
                id: `moodboard-additional-${img.id}`,
                url: img.image_url,
                type: 'moodboard',
                serviceType: 'moodboard',
                createdAt: img.created_at,
                description: img.image_name 
                  ? `Moodboard - ${img.image_name}${img.is_primary ? ' (Primary)' : ''}`
                  : `Moodboard Generation ${img.is_primary ? '(Primary)' : `#${img.sort_order}`}`
              });
            });
          } else {
            console.log('No additional moodboard images found in database');
          }
        }
      } catch (additionalImagesError) {
        console.warn('Could not fetch additional moodboard images:', additionalImagesError);
        // Continue without additional images if the table doesn't exist
      }

      // Fetch upstaging generations
      const { data: upstagingGenerations, error: upstagingError } = await supabase
        .from('upstaging_generations')
        .select('id, original_image_url, upstaged_image_url, created_at, prompt, style')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (upstagingError) {
        console.error('Error fetching user upstaging images:', upstagingError);
      } else if (upstagingGenerations) {
        upstagingGenerations.forEach(gen => {
          if (gen.original_image_url) {
            images.push({
              id: `upstaging-original-${gen.id}`,
              url: gen.original_image_url,
              type: 'upstaging-original',
              serviceType: 'upstaging',
              createdAt: gen.created_at,
              prompt: gen.prompt,
              description: `Original (${gen.style})`
            });
          }
          if (gen.upstaged_image_url) {
            images.push({
              id: `upstaging-result-${gen.id}`,
              url: gen.upstaged_image_url,
              type: 'upstaging-result',
              serviceType: 'upstaging',
              createdAt: gen.created_at,
              prompt: gen.prompt,
              description: `Upstaged (${gen.style})`
            });
          }
        });
      }

      // Fetch AI touchup operations
      const { data: touchupOperations, error: touchupError } = await supabase
        .from('ai_touchup_operations')
        .select('id, original_image_url, result_image_url, created_at, prompt, operation_type')
        .eq('user_id', userId)
        .eq('processing_status', 'completed')
        .order('created_at', { ascending: false });

      if (touchupError) {
        console.error('Error fetching user touchup images:', touchupError);
      } else if (touchupOperations) {
        touchupOperations.forEach(op => {
          if (op.original_image_url) {
            images.push({
              id: `touchup-original-${op.id}`,
              url: op.original_image_url,
              type: 'touchup-original',
              serviceType: 'touchup',
              createdAt: op.created_at,
              prompt: op.prompt,
              description: `Original (${op.operation_type})`
            });
          }
          if (op.result_image_url) {
            images.push({
              id: `touchup-result-${op.id}`,
              url: op.result_image_url,
              type: 'touchup-result',
              serviceType: 'touchup',
              createdAt: op.created_at,
              prompt: op.prompt,
              description: `${op.operation_type === 'erase' ? 'Erased' : 'Changed'} Result`
            });
          }
        });
      }

      // Fetch furniture removal operations
      try {
        const furnitureOperations = await furnitureRemovalService.getUserOperations(userId);
        
        furnitureOperations.forEach(op => {
          if (op.original_image_url) {
            images.push({
              id: `furniture-original-${op.id}`,
              url: op.original_image_url,
              type: 'furniture-original',
              serviceType: 'furniture',
              createdAt: op.created_at,
              prompt: op.operation_type === 'clean_selected' ? op.clean_selected_prompt : undefined,
              description: `Original (${op.operation_type === 'clean_selected' ? 'Clean Selected' : 'Clear All'})`
            });
          }
          if (op.result_image_url) {
            images.push({
              id: `furniture-result-${op.id}`,
              url: op.result_image_url,
              type: 'furniture-result',
              serviceType: 'furniture',
              createdAt: op.created_at,
              prompt: op.operation_type === 'clean_selected' ? op.clean_selected_prompt : undefined,
              description: `${op.operation_type === 'clean_selected' ? 'Clean Selected' : 'Clear All'} Result`
            });
          }
        });
      } catch (furnitureError) {
        console.error('Error fetching furniture removal operations:', furnitureError);
      }

      // Fetch user gallery uploads
      try {
        const userUploads = await userGalleryService.getUserUploads(userId);
        userUploads.forEach(upload => {
          images.push({
            id: `user-upload-${upload.id}`,
            url: upload.fileUrl,
            type: 'user-upload',
            serviceType: 'gallery',
            createdAt: upload.createdAt,
            description: upload.originalFilename
          });
        });
      } catch (uploadError) {
        console.error('Error fetching user uploads:', uploadError);
      }

      // Sort by creation date (newest first) and remove duplicates
      const uniqueImages = images.filter((image, index, self) => 
        index === self.findIndex(img => img.url === image.url)
      );

      return uniqueImages.sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    } catch (error) {
      console.error('Error fetching user images:', error);
      return [];
    }
  },

  // Get images filtered by service type
  async getUserImagesByService(userId: string, serviceType: 'moodboard' | 'upstaging' | 'touchup' | 'gallery' | 'furniture'): Promise<UserImage[]> {
    const allImages = await this.getUserImages(userId);
    return allImages.filter(image => image.serviceType === serviceType);
  },

  // Debug function to check multiple images setup
  async debugMultipleImagesSetup(userId: string): Promise<void> {
    console.log('🔍 Debugging multiple images setup for user:', userId);
    
    try {
      // Check if table exists
      const { error: tableError } = await supabase
        .from('moodboard_generation_images')
        .select('count', { count: 'exact', head: true });
      
      if (tableError) {
        console.log('❌ moodboard_generation_images table does not exist:', tableError);
        return;
      }
      
      console.log('✅ moodboard_generation_images table exists');
      
      // Check user's generations
      const { data: generations, error: genError } = await supabase
        .from('moodboard_generations')
        .select('id, created_at, status')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(5);
      
      if (genError) {
        console.log('❌ Error fetching generations:', genError);
        return;
      }
      
      console.log(`📊 Found ${generations?.length || 0} recent generations`);
      
      if (generations && generations.length > 0) {
        const generationIds = generations.map(g => g.id);
        
        // Check for additional images
        const { data: additionalImages, error: imgError } = await supabase
          .from('moodboard_generation_images')
          .select('generation_id, image_url, is_primary, sort_order')
          .in('generation_id', generationIds);
        
        if (imgError) {
          console.log('❌ Error fetching additional images:', imgError);
          return;
        }
        
        console.log(`🖼️ Found ${additionalImages?.length || 0} additional images total`);
        
        generations.forEach(gen => {
          const genImages = additionalImages?.filter(img => img.generation_id === gen.id) || [];
          console.log(`  Generation ${gen.id}: ${genImages.length} images`);
        });
      }
    } catch (error) {
      console.log('❌ Debug error:', error);
    }
  },

  // Convert a URL to a File-like object for processing
  async urlToFile(url: string, filename: string = 'selected-image.jpg'): Promise<File> {
    console.log('🔄 Converting URL to File:', url);
    
    // Validate URL format
    if (!url || !url.startsWith('http')) {
      throw new Error('Invalid image URL format. URL must start with http:// or https://');
    }
    
    try {
      // First try direct fetch (works for same-origin or CORS-enabled URLs)
      try {
        const response = await fetch(url, {
          mode: 'cors',
          headers: {
            'Accept': 'image/*'
          }
        });
        
        if (response.ok) {
          const blob = await response.blob();
          console.log('✅ Direct fetch successful for:', url);
          return new File([blob], filename, {
            type: blob.type || 'image/jpeg',
            lastModified: Date.now()
          });
        }
        
        // Provide specific error based on status code
        if (response.status === 403) {
          throw new Error('Access forbidden. The image server is blocking cross-origin requests (CORS issue).');
        } else if (response.status === 404) {
          throw new Error('Image not found. The URL may be expired or the image has been removed.');
        } else if (response.status >= 500) {
          throw new Error('Server error. The image server is temporarily unavailable.');
        } else {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
      } catch (fetchError) {
        console.log('❌ Direct fetch failed, trying alternative method:', fetchError);
        
        // If it's a CORS error, provide specific guidance
        if (fetchError instanceof Error && fetchError.message.includes('CORS')) {
          console.log('🔄 CORS error detected, trying canvas fallback...');
        }
      }

      // Fallback: Use Image element + Canvas approach (more CORS-friendly)
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous'; // Try to enable CORS
        
        img.onload = () => {
          try {
            // Create canvas and draw image
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            if (!ctx) {
              throw new Error('Failed to get canvas context');
            }

            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            ctx.drawImage(img, 0, 0);

            // Convert canvas to blob
            canvas.toBlob(
              (blob) => {
                if (blob) {
                  const file = new File([blob], filename, {
                    type: 'image/jpeg',
                    lastModified: Date.now()
                  });
                  resolve(file);
                } else {
                  reject(new Error('Failed to convert canvas to blob'));
                }
              },
              'image/jpeg',
              0.95
            );
          } catch (canvasError) {
            console.error('Canvas conversion failed:', canvasError);
            reject(new Error('Failed to process image after loading'));
          }
        };

        img.onerror = () => {
          console.error('Image loading failed for URL:', url);
          
          // Check if this is an R2 URL and provide specific guidance
          if (url.includes('r2.dev') || url.includes('cloudflarestorage.com')) {
            reject(new Error('CORS Issue: The image server is blocking access from your browser due to security restrictions. Please configure CORS on your R2 bucket to allow your domain, or try uploading the image directly instead.'));
          } else {
            reject(new Error('Failed to load image. The image may be inaccessible due to CORS restrictions or the URL may be invalid.'));
          }
        };

        // Start loading the image
        img.src = url;
      });
    } catch (error) {
      console.error('Error converting URL to File:', error);
      
      // Re-throw the specific error if it's already detailed
      if (error instanceof Error && (
          error.message.includes('CORS') || 
          error.message.includes('forbidden') || 
          error.message.includes('not found') ||
          error.message.includes('unavailable')
        )) {
        throw error;
      }
      
      throw new Error('Failed to load selected image. Please try uploading the image directly instead.');
    }
  },

  // Special function for handling existing image selection with CORS workaround
  async selectExistingImage(imageData: UserImage): Promise<{ file: File; success: boolean; error?: string }> {
    console.log('🖼️ Selecting existing image:', imageData.description, 'URL:', imageData.url);
    
    try {
      // First try the normal urlToFile method
      const file = await this.urlToFile(imageData.url, `${imageData.id}.jpg`);
      console.log('✅ Successfully loaded existing image:', file.name, 'Size:', file.size);
      
      return { file, success: true };
    } catch (error) {
      console.error('❌ Failed to load existing image via urlToFile:', error);
      
      // If it's a CORS error, try a different approach
      if (error instanceof Error && error.message.includes('CORS')) {
        console.log('🔄 CORS error detected, trying alternative approach...');
        
        try {
          // Try to fetch the image without CORS mode first
          const response = await fetch(imageData.url, {
            mode: 'no-cors',
            headers: {
              'Accept': 'image/*'
            }
          });
          
          if (response.type === 'opaque') {
            // Opaque response means it worked but we can't access the content
            // This is actually good - it means the image is accessible
            console.log('✅ Image is accessible (opaque response), creating workaround file');
            
            // Create a File object that references the original URL
            const dummyBlob = new Blob([''], { type: 'image/jpeg' });
            const file = new File([dummyBlob], `${imageData.id}.jpg`, {
              type: 'image/jpeg',
              lastModified: Date.now()
            });
            
            // Add the original URL as a custom property for later use
            (file as any).originalUrl = imageData.url;
            (file as any).isExistingImage = true;
            (file as any).imageData = imageData;
            (file as any).isCorsWorkaround = true;
            
            console.log('✅ Created workaround file for existing image');
            return { file, success: true };
          }
        } catch (noCorsError) {
          console.log('❌ No-CORS fetch also failed:', noCorsError);
        }
        
        // Final fallback: Create a minimal file object
        try {
          const dummyBlob = new Blob([''], { type: 'image/jpeg' });
          const file = new File([dummyBlob], `${imageData.id}.jpg`, {
            type: 'image/jpeg',
            lastModified: Date.now()
          });
          
          // Add the original URL as a custom property for later use
          (file as any).originalUrl = imageData.url;
          (file as any).isExistingImage = true;
          (file as any).imageData = imageData;
          (file as any).isCorsWorkaround = true;
          
          console.log('✅ Created fallback file for existing image');
          return { file, success: true };
        } catch (workaroundError) {
          console.error('❌ Workaround also failed:', workaroundError);
          return { 
            file: new File([], 'error.jpg'), 
            success: false, 
            error: 'Failed to load existing image due to CORS restrictions. Please upload the image directly instead.' 
          };
        }
      }
      
      return { 
        file: new File([], 'error.jpg'), 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error occurred' 
      };
    }
  },

  // Function to get actual image data for CORS workaround files
  async getImageDataForWorkaround(file: File): Promise<File> {
    if (!(file as any).isCorsWorkaround || !(file as any).originalUrl) {
      return file; // Not a workaround file, return as-is
    }

    const originalUrl = (file as any).originalUrl;
    console.log('🔄 Getting actual image data for workaround file:', originalUrl);

    try {
      // Try to fetch the image using a different approach
      const response = await fetch(originalUrl, {
        mode: 'cors',
        headers: {
          'Accept': 'image/*',
          'Cache-Control': 'no-cache'
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const actualFile = new File([blob], file.name, {
          type: blob.type || 'image/jpeg',
          lastModified: Date.now()
        });
        
        console.log('✅ Successfully fetched actual image data:', actualFile.size, 'bytes');
        return actualFile;
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('❌ Failed to get actual image data:', error);
      
      // If we still can't get the data, provide a helpful error
      throw new Error('Unable to process this image due to CORS restrictions. Please upload the image directly instead.');
    }
  }
}; 